/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Hidden, TextField } from '@mui/material'
import { LabelWithIndicator, Typography } from '@carto/meridian-ds/components'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import { getProvider } from 'features/connections/providers'
import {
  Connection,
  OAuthSnowflakeState,
  ProviderType,
  SnowflakeConfigType,
  SnowflakeParams
} from 'features/connections/types'
import useIsIndividualUser from 'features/users/state/useIsIndividualUser'
import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { isValidConnectionName } from '../../../connection-validate'
import AdvancedOptions from '../AdvancedOptions/AdvancedOptions'
import AdvancedOptionsSection from '../AdvancedOptions/AdvancedOptionsSection'
import WorkflowsTempForm from '../AdvancedOptions/WorkflowsTempForm'
import AnalyticsToolboxBanner from '../AnalyticsToolboxBanner'
import PrivacyFieldForm from '../PrivacyFieldForm'
import DataObservatoryLocationForm from '../AdvancedOptions/DataObservatoryLocationForm'
import AnalyticsToolboxLocationForm from '../AdvancedOptions/AnalyticsToolboxLocationForm'
import { ExternalIdp, IDP } from 'features/integrations/types'
import SnowflakeConnectionOAuthUser from './SnowflakeConnectionOAuthUser'
import QueryConcurrencyControlForm from '../AdvancedOptions/QueryConcurrencyControlForm'
import FormHeaderSection from '../../FormHeaderSection'
import QueryAliasOnly from '../AdvancedOptions/QueryAliasOnly'

interface SnowflakeConnectionWithOAuthFormProps {
  connection?: Connection
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>
  selectedRole?: string
  oauthState?: OAuthSnowflakeState
  externalIdp?: ExternalIdp
  onRequestNewOauth: () => void
  hideAdvanced?: boolean
}

function SnowflakeConnectionWithOAuthForm({
  connection,
  form,
  selectedRole,
  oauthState,
  externalIdp,
  onRequestNewOauth,
  hideAdvanced = false
}: SnowflakeConnectionWithOAuthFormProps) {
  const [errorName, setErrorName] = useState<boolean>(false)
  const [showATBanner, setShowATBanner] = useState<boolean>(true)
  const isIndividualUser = useIsIndividualUser()
  const isNotIndividualUser = !isIndividualUser
  const provider = getProvider(ProviderType.SNOWFLAKE)
  const intl = useIntl()
  const { register, setValue } = form
  const credentials: SnowflakeParams = connection?.config.credentials as SnowflakeParams
  const nameIsNotValidMsg = intl.formatMessage({ id: 'connections.create.common.connectionName.error' })
  const queryConcurrencyControlEnabled = useFeatureFlag(FeatureFlag.QueryConcurrencyControl)
  const enableSnowflakeRowLevelSecurity = useFeatureFlag(FeatureFlag.SnowflakeRowLevelSecurity)
  const isOAuth = !!oauthState || connection?.config?.type === SnowflakeConfigType.OAuthToken
  const isInvalidOauth = isOAuth && !oauthState && connection?.invalidOAuth
  const type = SnowflakeConfigType.OAuthToken
  const validateName = (value: string) => {
    const isValidName = isValidConnectionName(value)
    setErrorName(!isValidName)
    return isValidName || nameIsNotValidMsg
  }

  const snowflakeUser =
    oauthState?.email || oauthState?.userId || credentials?.username || credentials?.client_email || ''

  const roleName = 'role'

  useEffect(() => {
    if (selectedRole) {
      setValue(roleName, selectedRole)
    }
  }, [selectedRole, setValue])
  const displayRole = !isOAuth || (isOAuth && (selectedRole || credentials?.role)) || !enableSnowflakeRowLevelSecurity
  return (
    <Box>
      <FormHeaderSection>
        <Hidden smUp>{provider.icon}</Hidden>
        <Box ml={0.75}>
          <Typography variant="subtitle2">
            <FormattedMessage id="connections.create.subtitle" values={{ provider: provider.label }} />
          </Typography>
        </Box>
      </FormHeaderSection>

      {showATBanner && (
        <AnalyticsToolboxBanner provider={ProviderType.SNOWFLAKE} onClose={() => setShowATBanner(false)} />
      )}
      <input type="hidden" {...register('connectionType', { required: true })} value={type} />
      {connection?.sharingScope && (
        <input type="hidden" {...register('sharingScope')} value={connection?.sharingScope} />
      )}
      {oauthState && <input type="hidden" {...register('state', { required: true })} value={oauthState.state} />}

      <input
        type="hidden"
        {...register('client_email', { required: true })}
        value={oauthState?.userId || credentials?.client_email}
      />
      <input type="hidden" {...register('external_idp_id', { required: true })} value={externalIdp?.id} />

      {(externalIdp?.idp === IDP.OKTA || externalIdp?.idp === IDP.AZURE) && (
        <input
          type="hidden"
          {...register('username', { required: true })}
          value={oauthState?.email || credentials?.username}
        />
      )}

      {externalIdp?.idp === IDP.CUSTOM && (
        <input
          type="hidden"
          {...register('username', { required: true })}
          value={oauthState?.userId || credentials?.username}
        />
      )}

      <TextField
        sx={{ mt: (theme) => theme.spacing(2.5) }}
        name="name"
        size="small"
        inputProps={{
          'aria-label': 'name',
          ...register('name', { required: true, validate: validateName })
        }}
        InputProps={{
          readOnly: !!connection?.id
        }}
        defaultValue={connection?.name}
        placeholder={intl.formatMessage({ id: 'connections.create.snowflake.form.connectionNamePlaceholder' })}
        label={intl.formatMessage({ id: 'connections.create.snowflake.form.connectionNameLabel' })}
        helperText={errorName && nameIsNotValidMsg}
        error={errorName}
      />
      <SnowflakeConnectionOAuthUser
        form={form}
        isInvalidOauth={!!isInvalidOauth}
        onRequestNewOauth={onRequestNewOauth}
        snowflakeUser={snowflakeUser}
      />
      <TextField
        sx={{ mt: (theme) => theme.spacing(2.5) }}
        name="account"
        size="small"
        inputProps={{
          'aria-label': 'account',
          readOnly: isOAuth,
          ...register('account', { required: true })
        }}
        defaultValue={credentials?.account || (isOAuth ? externalIdp?.snowflakeAccountName : '')}
        placeholder={intl.formatMessage({ id: 'connections.create.snowflake.form.accountPlaceholder' })}
        label={intl.formatMessage({ id: 'connections.create.snowflake.form.accountLabel' })}
      />
      <Box>
        <TextField
          sx={{ mt: (theme) => theme.spacing(2.5) }}
          name="warehouse"
          size="small"
          inputProps={{ 'aria-label': 'warehouse', ...register('warehouse', { required: false }) }}
          defaultValue={credentials?.warehouse}
          placeholder={intl.formatMessage({ id: 'connections.create.snowflake.form.warehousePlaceholder' })}
          label={
            <LabelWithIndicator
              label={<FormattedMessage id="connections.create.snowflake.form.warehouseLabel" />}
              type="optional"
            />
          }
        />
      </Box>
      <Box>
        <TextField
          sx={{ mt: (theme) => theme.spacing(2.5) }}
          name="database"
          size="small"
          inputProps={{ 'aria-label': 'database', ...register('database', { required: true }) }}
          defaultValue={connection?.config.credentials.database}
          placeholder={intl.formatMessage({ id: 'connections.create.snowflake.form.databasePlaceholder' })}
          label={`${intl.formatMessage({ id: 'connections.create.snowflake.form.databaseLabel' })}`}
        />
      </Box>
      {displayRole && (
        <Box>
          <TextField
            sx={{ mt: (theme) => theme.spacing(2.5) }}
            name={roleName}
            size="small"
            inputProps={{
              'aria-label': 'role',
              readOnly: isOAuth,
              ...register(roleName)
            }}
            defaultValue={selectedRole || connection?.config.credentials.role}
            placeholder={intl.formatMessage({ id: 'connections.create.snowflake.form.rolePlaceholder' })}
            label={
              <LabelWithIndicator
                label={<FormattedMessage id="connections.create.snowflake.form.roleLabel" />}
                type="optional"
              />
            }
          />
        </Box>
      )}
      {isNotIndividualUser && (!isOAuth || enableSnowflakeRowLevelSecurity) && (
        <PrivacyFieldForm
          form={form}
          providerId={ProviderType.SNOWFLAKE}
          connection={connection}
          connectionConfigType={SnowflakeConfigType.OAuthToken}
        />
      )}
      {!hideAdvanced && (
        <AdvancedOptions>
          <AdvancedOptionsSection titleId="connections.create.advanced.analyticsToolboxDataLocationTitle">
            <AnalyticsToolboxLocationForm form={form} connection={connection} provider={ProviderType.SNOWFLAKE} />
            <DataObservatoryLocationForm form={form} connection={connection} />
            <WorkflowsTempForm form={form} connection={connection} provider={ProviderType.SNOWFLAKE} />
          </AdvancedOptionsSection>
          {queryConcurrencyControlEnabled && (
            <AdvancedOptionsSection titleId="connections.create.advanced.queryConcurrencyControl.optionsTitle">
              <QueryConcurrencyControlForm form={form} connection={connection} />
            </AdvancedOptionsSection>
          )}
          <AdvancedOptionsSection titleId="connections.create.advanced.developerOptions.title">
            <QueryAliasOnly form={form} connection={connection} />
          </AdvancedOptionsSection>
        </AdvancedOptions>
      )}
    </Box>
  )
}

export default SnowflakeConnectionWithOAuthForm
